/**
 * RTK Query Endpoint Overrides
 * =============================
 *
 * Extends auto-generated entitlementsApi.ts with:
 * - Custom response handlers (e.g., blob/file downloads)
 * - Cache invalidation via generic controller-level tags
 *
 * AFTER REGENERATING API (via OpenApi):
 * - Verify endpoint names haven't changed (enrollAssets, updatePaymentMethod, etc.)
 * - Add overrides for new endpoints requiring custom behavior and adjust tags as needed
 */

import {
  type DownloadInvoicesApiArg,
  type EnrollAssetsApiArg,
  type EnrollAssetsApiResponse,
  type ExportDiscountsApiArg,
  entitlementsApi,
  type UnenrollAssetsApiArg,
  type UnenrollAssetsApiResponse,
} from './entitlementsApi';

export interface DownloadInvoicesResponse {
  url: string;
  contentType: string;
}

export interface ExportDiscountsResponse {
  url: string;
  contentType: string;
}

/**
 * Override for downloadInvoices mutation
 *
 * Replaces auto-generated endpoint to provide custom blob handling:
 * - Converts blob response to object URL for browser download
 * - Returns { url, contentType } instead of raw Blob
 * - Does not invalidate invoice-controller tag (downloading doesn't change invoice list)
 */
entitlementsApi.injectEndpoints({
  endpoints: (build) => ({
    downloadInvoices: build.mutation<
      DownloadInvoicesResponse,
      DownloadInvoicesApiArg
    >({
      query: (queryArg) => ({
        url: `/billing/2025-06-11/organizations/${queryArg.orgId}/invoices/files`,
        method: 'POST',
        body: queryArg.invoiceDownloadRequestDto,
        headers: {
          Accept: 'application/pdf, application/zip, */*',
        },
        responseHandler: async (response) => {
          if (!response.ok) {
            const errText = await response.text();
            const error = new Error(errText);
            (error as Error & { status?: number }).status = response.status;
            throw error;
          }
          const blob = await response.blob();
          return { url: URL.createObjectURL(blob), contentType: blob.type };
        },
      }),
    }),
    /**
     * Override for exportDiscounts mutation
     *
     * Returns a blob URL for browser download (CSV). Does not invalidate discount list.
     */
    exportDiscounts: build.mutation<
      ExportDiscountsResponse,
      ExportDiscountsApiArg
    >({
      query: (queryArg) => ({
        url: '/billing/2026-02-10/discounts/export',
        method: 'POST',
        body: queryArg.discountFilter,
        params: {
          locale: queryArg.locale,
          timeZone: queryArg.timeZone,
        },
        headers: {
          Accept: 'text/csv, application/json, */*',
        },
        responseHandler: async (response) => {
          if (!response.ok) {
            const errText = await response.text();
            const error = new Error(errText);
            (error as Error & { status?: number }).status = response.status;
            throw error;
          }
          const blob = await response.blob();
          return { url: URL.createObjectURL(blob), contentType: blob.type };
        },
      }),
    }),
    /**
     * Override for enrollAssets mutation
     *
     * `waitOnAiSearchSync` defaults to `true` to overcome latency with Azure AI
     * Search synchronization. Callers can opt out by passing
     * `waitOnAiSearchSync: false` in the DTO — e.g. the activation flow, which
     * needs the enroll to return asynchronously with a `batchId`/`batchSize`
     * instead of blocking and completing synchronously.
     */
    enrollAssets: build.mutation<EnrollAssetsApiResponse, EnrollAssetsApiArg>({
      query: (queryArg) => ({
        url: '/billing/2025-06-11/orders/enroll',
        method: 'POST',
        body: {
          ...queryArg.requestEnrollAssetsDto,
          waitOnAiSearchSync:
            queryArg.requestEnrollAssetsDto.waitOnAiSearchSync ?? true,
        },
      }),
    }),
    /**
     * Override for unenrollAssets mutation
     * Apply waitOnAiSearchSync option to overcome latency with AI Search synchronization
     */
    unenrollAssets: build.mutation<
      UnenrollAssetsApiResponse,
      UnenrollAssetsApiArg
    >({
      query: (queryArg) => ({
        url: '/billing/2025-06-11/orders/unenroll',
        method: 'POST',
        body: {
          ...queryArg.requestUnenrollAssetsDto,
          waitOnAiSearchSync: true,
        },
      }),
    }),
  }),
  overrideExisting: true,
});

/**
 * Tag Management:
 * Add and/or modify tags as needed for greater cache control
 *
 * Payment mutations are auto-tagged with `payment-controller` but `getOrganizationBillingAccount`
 * only provides `billing-controller`. To ensure billing UI refreshes after real payment changes,
 * payment mutations (addPaymentMethod, updatePaymentMethod, etc.) are overridden to invalidate
 * `billing-controller`. `createPaymentSession1` is stripped of all invalidation tags because it
 * only fetches a Zuora session token — it doesn't modify state and was causing the payment form
 * to re-render.
 *
 * Discount mutations (apply / change / cancel) are auto-tagged with `discount-controller`, but
 * the active `DiscountSummaryDto` is embedded in `entitlements-controller`-tagged responses
 * (subscription details / eligibility). Cross-invalidate so the plan UI refetches after a write.
 */

entitlementsApi.enhanceEndpoints({
  endpoints: {
    // --- Assets ---
    enrollAssets: {
      invalidatesTags: () => [
        'billing-controller',
        'entitlements-controller',
        'asset-license-controller',
      ],
    },

    unenrollAssets: {
      invalidatesTags: () => [
        'billing-controller',
        'entitlements-controller',
        'asset-license-controller',
      ],
    },

    // --- Orders ---
    createOrder: {
      invalidatesTags: () => [
        'billing-controller',
        'entitlements-controller',
        'asset-license-controller',
      ],
    },

    // --- Discounts ---
    // The active discount is embedded in `entitlements-controller`-tagged
    // subscription responses, so apply / change / cancel must invalidate both
    // controllers to keep plan UI and the discount list in sync.
    applyDiscountToSubscription: {
      invalidatesTags: () => ['discount-controller', 'entitlements-controller'],
    },

    changeDiscount: {
      invalidatesTags: () => ['discount-controller', 'entitlements-controller'],
    },

    cancelDiscount: {
      invalidatesTags: () => ['discount-controller', 'entitlements-controller'],
    },

    // --- Order Previews ---
    // Strip each preview's auto-generated controller tag; previews are
    // stateless computations that don't need to refetch when other state changes.
    previewOrder: {
      providesTags: () => [],
    },
    previewEnrollAssets: {
      providesTags: () => [],
    },
    previewPeripheralOrder: {
      providesTags: () => [],
    },

    // --- Billing account ---
    getOrganizationBillingAccount: {
      providesTags: () => ['billing-controller'],
    },

    // --- Payments ---
    createPaymentSession1: {
      invalidatesTags: () => [],
    },

    updatePaymentMethod: {
      invalidatesTags: () => ['billing-controller'],
    },

    addPaymentMethod: {
      invalidatesTags: () => ['billing-controller'],
    },

    setDefaultPaymentMethod: {
      invalidatesTags: () => ['billing-controller'],
    },

    deletePaymentMethod: {
      invalidatesTags: () => ['billing-controller'],
    },

    createShippingAddress: {
      invalidatesTags: () => ['billing-controller'],
    },

    updateShippingAddress: {
      invalidatesTags: () => ['billing-controller'],
    },
  },
});

export { entitlementsApi };
